import Button from "@elements/button";
import AreaField from "@elements/fields/areaField";
import MapField from "@elements/fields/mapField";
import NumberField from "@elements/fields/numberField";
import PhoneField from "@elements/fields/phoneField";
import TextField from "@elements/fields/textField";
import {
  IAddressFieldsInput,
  IAreaDetailInput,
} from "@types";
import Loading from "assets/lottie/loading";
import { useTranslations } from "next-intl";
import useAddressForm from "@hooks/useAddressForm";

interface AddressFormProps {
  area: IAreaDetailInput;
  mapValue: { street: string; lat: number; lng: number };
  defaultValue?: IAddressFieldsInput;
  id?: number;
  useMap?: boolean;
  setUseMap?: (data: boolean) => void;
  changeStep?: (data: "form" | "map" | "area") => void;
  onSubmit: (data: IAddressFieldsInput) => void;
  changeArea: (data: IAreaDetailInput) => void;
  changeMap: (data: { street: string; lat: number; lng: number }) => void;
  setMapPosition?: (data: { street: string; lat: number; lng: number }) => void;
  onAutofillAreaChange?: (areaId: number) => void;
}

const AddressForm: React.FC<AddressFormProps> = ({
  defaultValue,
  id,
  area,
  mapValue,
  useMap,
  setUseMap,
  onSubmit,
  changeArea,
  changeMap,
  setMapPosition,
  onAutofillAreaChange,
}) => {
  const t = useTranslations();
  const { isLoading, isAutofillLoading, value, data, errors, isValid, handelChangeValue, handelUseMap, handelSubmit } = useAddressForm({ area, defaultValue, id, setMapPosition, setUseMap, useMap, onAutofillAreaChange, onSubmit })

  return (
    <div className={`pb-5 w-full md:${data?.break ? "min-w-[800px]" : "min-w-[400px]"}`}>
      {(isLoading || isAutofillLoading) && <Loading isCenter />}
      {data?.items && (
        <div className="w-full mt-10">
          <div className="flex flex-wrap w-full mx-auto form">
            {data.break && (
              <div className="lg:w-1/2 md:w-full w-full flex flex-col px-1 justify-start">
                {data.items.map((row, index) => {
                  if (index < data.break) {
                    return (
                      <div
                        key={`main-${row.items[0].key}`}
                        className="md:w-full sm:w-full xs:w-full w-full mb-3"
                      >
                        <div className="flex flex-wrap">
                          {row.items.map((field) => (
                            <div
                              key={`field-${field.key}-${field.title}`}
                              className="flex-1"
                            >
                              {field.type === "area" && (
                                <AreaField
                                  area={area}
                                  onChange={(v) => {
                                    handelChangeValue(
                                      field.key,
                                      v.id,
                                      field.validations,
                                      field.depend,
                                      field.reset
                                    );
                                    changeArea(v);
                                  }}
                                />
                              )}
                              {field.type === "text" && (
                                <TextField
                                  defaultValue={value[field.key] ?? field.value}
                                  onChange={(v) =>
                                    handelChangeValue(
                                      field.key,
                                      v,
                                      field.validations,
                                      field.depend,
                                      field.reset
                                    )
                                  }
                                  errors={errors[`${field.key}` as any] || []}
                                  label={`${field.title} ${field.validations.some(item => item.type === "required") ? "" : t("client_general_optional")}`}
                                />
                              )}
                              {field.type === "number" && (
                                <NumberField
                                  defaultValue={value[field.key] ?? field.value}
                                  onChange={(v) =>
                                    handelChangeValue(
                                      field.key,
                                      v,
                                      field.validations,
                                      field.depend,
                                      field.reset
                                    )
                                  }
                                  errors={errors[`${field.key}` as any] || []}
                                  label={`${field.title} ${field.validations.some(item => item.type === "required") ? "" : t("client_general_optional")}`}
                                />
                              )}
                              {field.type === "map" && (
                                <MapField
                                  isEdit={id ? true : false}
                                  useMap={useMap}
                                  onChange={(v) =>
                                    handelChangeValue(
                                      field.key,
                                      v.value,
                                      field.validations,
                                      field.depend,
                                      field.reset,
                                      v.street,
                                      v.newFormValues
                                    )
                                  }
                                  setUseMap={handelUseMap}
                                  defaultAddress={mapValue}
                                  area={area}
                                  changeMapValue={changeMap}
                                  label={`${field.title} ${field.validations.some(item => item.type === "required") ? "" : t("client_general_optional")}`}
                                />
                              )}
                              {field.type === "phone" && (
                                <PhoneField
                                defaultValue={value[field.key] ?? field.value}
                                onChange={(v) =>
                                    handelChangeValue(
                                      field.key,
                                      v,
                                      field.validations,
                                      field.depend,
                                      field.reset
                                    )
                                  }
                                  errors={errors[`${field.key}` as any] || []}
                                  label={`${field.title} ${field.validations.some(item => item.type === "required") ? "" : t("client_general_optional")}`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            )}
            <div
              className={`${data.break ? " lg:w-1/2 md:w-full w-full" : "w-full"
                }  flex flex-col px-1 justify-start`}
            >
              {data.items.map((row, index) => {
                if (index >= data.break) {
                  return (
                    <div
                      key={`main-${row.items[0].key}`}
                      className="md:w-full sm:w-full xs:w-full w-full mb-3"
                    >
                      <div className="flex flex-wrap">
                        {row.items.map((field) => (
                          <div
                            key={`field-${field.key}-`}
                            className="flex-1"
                          >
                            {field.type === "area" && (
                              <AreaField
                                area={area}
                                onChange={(v) => {
                                  handelChangeValue(
                                    field.key,
                                    v,
                                    field.validations,
                                    field.depend,
                                    field.reset
                                  );
                                  changeArea(v);
                                }}
                              />
                            )}
                            {field.type === "text" && (
                              <TextField
                                defaultValue={value[field.key] ?? field.value}
                                onChange={(v) =>
                                  handelChangeValue(
                                    field.key,
                                    v,
                                    field.validations,
                                    field.depend,
                                    field.reset
                                  )
                                }
                                errors={errors[`${field.key}` as any] || []}
                                label={`${field.title} ${field.validations.some(item => item.type === "required") ? "" : t("client_general_optional")}`}
                              />
                            )}
                            {field.type === "number" && (
                              <NumberField
                                defaultValue={value[field.key] ?? field.value}
                                onChange={(v) =>
                                  handelChangeValue(
                                    field.key,
                                    v,
                                    field.validations,
                                    field.depend,
                                    field.reset
                                  )
                                }
                                errors={errors[`${field.key}` as any] || []}
                                label={`${field.title} ${field.validations.some(item => item.type === "required") ? "" : t("client_general_optional")}`}
                              />
                            )}
                            {field.type === "map" && (
                              <MapField
                                isEdit={id ? true : false}
                                useMap={useMap}
                                onChange={(v) =>
                                  handelChangeValue(
                                    field.key,
                                    v.value,
                                    field.validations,
                                    field.depend,
                                    field.reset,
                                    v.street,
                                    v.newFormValues
                                  )
                                }
                                setUseMap={handelUseMap}
                                defaultAddress={mapValue}
                                area={area}
                                changeMapValue={changeMap}
                                label={`${field.title} ${field.validations.some(item => item.type === "required") ? "" : t("client_general_optional")}`}
                              />
                            )}
                            {field.type === "phone" && (
                              <PhoneField
                                defaultValue={value[field.key] ?? field.value}
                                onChange={(v) =>
                                  handelChangeValue(
                                    field.key,
                                    v,
                                    field.validations,
                                    field.depend,
                                    field.reset
                                  )
                                }
                                errors={errors[`${field.key}` as any] || []}
                                label={`${field.title} ${field.validations.some(item => item.type === "required") ? "" : t("client_general_optional")}`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
          <Button
            size="slim"
            className="h-12 shadow-md font-extrabold text-base mb-2 m-auto rounded-full w-72 mt-3"
            type="submit"
            isDisabled={isLoading || isAutofillLoading || !isValid}
            loading={isLoading || isAutofillLoading}
            theme="blue"
            onClick={handelSubmit}
          >
            {t("w-saveAddress")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AddressForm;

